// ─── API Types ───────────────────────────────────────────────────────────────

export type DecisionUrgency = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW'
export type DecisionStatus  = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED'

/** Agent-authored, plain-English work product the dashboard renders verbatim. */
export interface DecisionWorkProduct {
  kind: 'email' | 'message' | 'article' | 'text' | 'action'
  heading?: string | null
  body: string
}

/**
 * Agent-authored context lifted out of `detail` by the API so Mark can tell,
 * at a glance, what a decision is about and what accepting vs rejecting does.
 * Every field is nullable — older decisions predate these fields, and not every
 * agent supplies every field for every decision type.
 */
export interface DecisionContext {
  description: string | null
  reason: string | null
  acceptDescription: string | null
  rejectDescription: string | null
  acceptAction: string | null
  workProduct: DecisionWorkProduct | null
}

export interface Decision {
  id: string
  skillName: string
  decisionType: string
  urgency: string
  summary: string
  detail: unknown
  options: unknown
  safeDefault: string
  timeoutAt: string
  status: string | null
  resolvedBy: string | null
  resolvedAt: string | null
  resolvedAction: string | null
  resolutionNote: string | null
  surfacesUsed: string[] | null
  createdAt: string
  /** Present on responses from the decisions API; may be absent on legacy
   *  SSE payloads, so always treat as optional. */
  context?: DecisionContext | null
}

/** Tab-header counts returned alongside every decisions list response. */
export interface DecisionCounts {
  pending: number
  approved: number
  rejected: number
}

export interface DecisionListResponse {
  data: Decision[]
  counts: DecisionCounts
}

export interface ActivityEntry {
  id: string
  agentId: string
  agentName: string
  action: string
  summary: string
  metadata: Record<string, unknown>
  createdAt: string
}

export interface ServiceHealth {
  name: string
  status: 'healthy' | 'degraded' | 'down'
  responseTimeMs: number
  checkedAt: string
  detail?: string
}

export interface HealthResponse {
  status: 'ok' | 'degraded'
  timestamp: string
  database: 'connected' | 'disconnected'
  tables: Record<string, boolean>
  version: string
}

export interface StatsResponse {
  decisionsToday: number
  pendingDecisions: number
  activationsLast24h: number
  avgResponseTimeMs: number
  updatedAt: string
}

export interface ApiResponse<T> {
  data: T
  meta?: {
    total: number
    page: number
    limit: number
  }
}

// ─── Base fetch ──────────────────────────────────────────────────────────────

const BASE = import.meta.env.VITE_API_URL ?? ''

/** Public base URL for the runtime API — '' means same-origin. Used by the
 *  SSE client (which builds an EventSource URL outside of apiFetch). */
export const API_BASE = BASE

// Clerk token management — registered by AuthTokenSync component.
// The dashboard authenticates exclusively via Clerk JWT. A previous build
// shipped a VITE_API_KEY fallback inside the public JS bundle, leaking the
// runtime API key to every browser that loaded the dashboard. That fallback
// is intentionally removed — if there is no Clerk token, requests must fail.
let _getToken: (() => Promise<string | null>) | null = null
let _cachedToken: string | null = null

/**
 * Register Clerk's getToken function for authenticated API calls and prime
 * the synchronous cache. Returns the priming promise so callers can await
 * `_cachedToken` being populated before they start firing API requests that
 * rely on authHeaders().
 */
export function registerAuth(getToken: () => Promise<string | null>): Promise<void> {
  _getToken = getToken
  return getToken()
    .then((t) => { _cachedToken = t })
    .catch(() => { _cachedToken = null })
}

/** Auth headers for direct fetch() calls that bypass apiFetch (sync, uses cached token). */
export function authHeaders(): Record<string, string> {
  if (_cachedToken) return { Authorization: `Bearer ${_cachedToken}` }
  return {}
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  if (_getToken) {
    const token = await _getToken()
    if (token) {
      _cachedToken = token
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const res = await fetch(`${BASE}${path}`, {
    headers: { ...headers, ...init?.headers },
    ...init,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw new Error(`API ${res.status}: ${text}`)
  }
  return res.json() as Promise<T>
}

// ─── Decision endpoints ───────────────────────────────────────────────────────

export const decisionsApi = {
  list: (status?: DecisionStatus) => {
    const qs = status ? `?status=${status}` : ''
    return apiFetch<DecisionListResponse>(`/api/v1/decisions${qs}`)
  },

  approve: (id: string) =>
    apiFetch<Decision>(`/api/v1/decisions/${id}/approve`, { method: 'POST' }),

  reject: (id: string, reason?: string) =>
    apiFetch<Decision>(`/api/v1/decisions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
}

// ─── Activity endpoints ───────────────────────────────────────────────────────

export const activityApi = {
  list: (limit = 50, cursor?: string) => {
    const qs = new URLSearchParams({ limit: String(limit) })
    if (cursor) qs.set('cursor', cursor)
    return apiFetch<ApiResponse<ActivityEntry[]>>(`/api/v1/activity?${qs}`)
  },
}

// ─── Health endpoint ──────────────────────────────────────────────────────────

export const healthApi = {
  get: () => apiFetch<HealthResponse>('/health'),
}

// ─── Stats endpoint ───────────────────────────────────────────────────────────

export const statsApi = {
  get: () => apiFetch<StatsResponse>('/api/v1/stats'),
}

// ─── Agents endpoint ──────────────────────────────────────────────────────────

export interface AgentSummary {
  name: string
  display_name: string
  emoji: string
  domain: string
  tier: 'council' | 'guardian' | 'infrastructure'
  model: string
  status: string
  last_run_at: string | null
  last_run_outcome: string | null
  runs_today: number
  runs_7d: number
  success_rate_24h: number | null
  success_rate_7d: number | null
  pending_decisions: number
  schedule_summary: string | null
  schedule_cron: string | null
  /** TMA-1298: cron ticks skipped because the skill is listed in DISABLED_CRON_SKILLS. */
  disabled_by_env?: boolean
}

export const agentsApi = {
  list: () => apiFetch<ApiResponse<AgentSummary[]>>('/api/v1/agents'),
}

// ─── Agent Inspector endpoints ───────────────────────────────────────────────
// Read-only views of an agent's memory store and skill configuration. Backs the
// slide-out Agent Inspector panel on the Agents page.

export interface AgentMemoryEntry {
  namespace: string
  key: string
  /** Sensitive keys (encrypted-at-rest or credential-shaped) are redacted. */
  sensitive: boolean
  redacted: boolean
  /** Null when redacted; otherwise the stored JSON value. */
  value: unknown
  version: number
  updated_at: string | null
}

export interface AgentMemoryResponse {
  skillName: string
  count: number
  keys: AgentMemoryEntry[]
}

export interface AgentToolInfo {
  name: string
  description: string
  /** A runtime handler is wired up (vs a config-only stub). */
  implemented: boolean
  /** Shared across every agent (read_memory, create_decision, …). */
  shared: boolean
}

export interface AgentScheduleEntry {
  cron: string
  description: string
  humanized: string
  timezone: string
}

export interface AgentSkillsResponse {
  skillName: string
  display_name: string
  emoji: string
  domain: string
  tier: 'council' | 'guardian' | 'infrastructure'
  model: string
  status: string
  version: string | null
  description: string | null
  loaded_at: string | null
  loaded: boolean
  tools: AgentToolInfo[]
  schedule: AgentScheduleEntry[]
  memory: { read_namespaces: string[]; write_namespaces: string[] } | null
  decision: {
    max_autonomous_actions: number
    requires_approval_for: string[]
    safe_default: string
    timeout_minutes: number
  } | null
}

export const agentInspectorApi = {
  memory: (skillName: string) =>
    apiFetch<AgentMemoryResponse>(`/api/v1/agents/${encodeURIComponent(skillName)}/memory`),
  skills: (skillName: string) =>
    apiFetch<AgentSkillsResponse>(`/api/v1/agents/${encodeURIComponent(skillName)}/skills`),
}

// ─── Integration Health endpoints (Hecate) ──────────────────────────────────

export type IntegrationStatus = 'healthy' | 'degraded' | 'down'

export interface IntegrationHealthItem {
  name: string
  slug: string
  status: IntegrationStatus
  responseTimeMs: number | null
  lastCheckAt: string
  errorCount24h: number
  consecutiveFailures: number
  baselineResponseTimeMs: number | null
  lastError: string | null
}

export interface IntegrationHealthResponse {
  data: IntegrationHealthItem[]
  summary: { total: number; healthy: number; degraded: number; down: number }
}

export const integrationsApi = {
  health: () => apiFetch<IntegrationHealthResponse>('/api/v1/integrations/health'),
}

// ─── Knowledge Base endpoints (Hestia) ───────────────────────────────────────

export interface StaleArticleRef {
  id: string
  title: string
  category: string
  daysSinceVerified: number | null
}

export interface KnowledgeRecentRef {
  id: string
  title: string
  category: string
  updatedAt: string | null
}

export interface KnowledgeStats {
  total: number
  stale: number
  byCategory: Record<string, number>
  lastVerifiedAt: string | null
  staleArticles: StaleArticleRef[]
  recent: KnowledgeRecentRef[]
}

export const knowledgeApi = {
  stats: () => apiFetch<{ data: KnowledgeStats }>('/api/v1/knowledge/stats'),
}

// ─── Market Intelligence endpoints ───────────────────────────────────────────

export interface CompetitorActivityItem {
  competitor: string
  type: string
  description: string
  timestamp: string
  source?: string
}

export interface RegulatoryUpdateItem {
  title: string
  link: string
  published: string
  summary: string
  source?: string
}

export const marketIntelApi = {
  competitorActivity: () =>
    apiFetch<{ namespace: string; key: string; value: CompetitorActivityItem[]; version: number }>(
      '/api/v1/memory/market-intel/competitors/activity',
    ).then((r) => (Array.isArray(r.value) ? r.value : [])),

  regulatoryLandscape: () =>
    apiFetch<{ namespace: string; key: string; value: RegulatoryUpdateItem[]; version: number }>(
      '/api/v1/memory/market-intel/regulatory/landscape',
    ).then((r) => (Array.isArray(r.value) ? r.value : [])),

  triggerScan: () =>
    apiFetch<{ success: boolean; correlationId: string; message: string }>(
      '/api/v1/agents/test/mnemosyne/scan',
      { method: 'POST', body: JSON.stringify({ trigger: 'manual' }) },
    ),
}

// ─── Token Usage endpoints ───────────────────────────────────────────────────

export interface AgentTokenSummary {
  name: string
  model: string
  tokens_24h: number
  cost_24h_usd: number
  tokens_7d: number
  cost_7d_usd: number
  tokens_30d: number
  cost_30d_usd: number
  success_rate: number
  total_runs: number
}

export interface TokenUsageSummaryResponse {
  agents: AgentTokenSummary[]
  total_cost_24h_usd: number
  total_cost_7d_usd: number
  total_cost_30d_usd: number
}

export interface HourlyBucket {
  hour: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  cost_usd: number
  runs: number
  successes: number
  failures: number
}

export interface AgentTokenDetailResponse {
  agent: string
  buckets: HourlyBucket[]
}

export const tokenUsageApi = {
  summary: () =>
    apiFetch<TokenUsageSummaryResponse>('/api/v1/token-usage/summary'),

  byAgent: (name: string) =>
    apiFetch<AgentTokenDetailResponse>(`/api/v1/token-usage/by-agent/${encodeURIComponent(name)}`),
}

// ─── Zeus Chat endpoints ──────────────────────────────────────────────────────

export interface ZeusChatMessage {
  role: 'user' | 'zeus'
  content: string
  timestamp: string
}

// POST /chat now returns 202 immediately — Zeus runs in the background.
export interface ZeusChatAcceptedResponse {
  accepted: boolean
  status: 'pending'
  conversationId: string
  messageId: string
  timestamp: string
}

export type ZeusChatResponseStatus = 'pending' | 'complete' | 'error'

// Shape of GET /chat/response/:conversationId
export interface ZeusChatResponseRecord {
  conversationId: string
  status: ZeusChatResponseStatus
  prompt: string
  response: string | null
  error: string | null
  createdAt: string
  completedAt: string | null
  tokensUsed: number
  durationMs: number
}

export const zeusChatApi = {
  send: (message: string) =>
    apiFetch<ZeusChatAcceptedResponse>('/api/v1/zeus/chat', {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  response: (conversationId: string) =>
    apiFetch<ZeusChatResponseRecord>(
      `/api/v1/zeus/chat/response/${encodeURIComponent(conversationId)}`,
    ),
  history: () =>
    apiFetch<{ messages: ZeusChatMessage[] }>('/api/v1/zeus/chat/history'),
}

// ─── Bridge endpoints (proxy to TMAgent product API) ─────────────────────────

export interface ComplianceAlert {
  id: string
  item_type: string
  days_remaining: number
  risk_level: 'critical' | 'high' | 'medium' | 'low'
  tenant_id: string
  tenant_name: string
  description: string
}

interface BridgeApproveResult {
  success: boolean
  id: string
  status: 'approved'
  actioned_at: string
}

interface BridgeRejectResult {
  success: boolean
  id: string
  status: 'rejected'
  actioned_at: string
}

interface BridgeResponse<T> {
  data: T
  source: 'tmagent-api' | 'unavailable'
  message?: string
}

export const bridgeApi = {
  approve: (id: string, approvedBy: string, notes?: string) =>
    apiFetch<BridgeResponse<BridgeApproveResult>>(`/api/bridge/approvals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: approvedBy, notes }),
    }),

  reject: (id: string, rejectedBy: string, reason: string) =>
    apiFetch<BridgeResponse<BridgeRejectResult>>(`/api/bridge/approvals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejected_by: rejectedBy, reason }),
    }),

  complianceAlerts: () =>
    apiFetch<BridgeResponse<ComplianceAlert[]>>('/api/bridge/compliance-alerts'),
}
